import { Controller } from "@hotwired/stimulus";
import { lockPage, unlockPage } from "../lib/page_lock";
import { TurboStreamEvent } from "@hotwired/turbo-rails";

export default class extends Controller {
  static targets = ["inputToggle", "element", "backdrop", "panel", "drawerPanel"];

  static values = {
    frameTarget: String,
  };

  declare readonly hasInputToggleTarget: boolean;
  declare readonly inputToggleTarget: HTMLInputElement;
  declare readonly hasElementTarget: boolean;
  declare readonly elementTarget: HTMLElement;
  // Optional sheet targets: when present, this modal renders as a responsive
  // drawer/bottom-sheet (see shared/_drawer_frame) instead of the DaisyUI
  // checkbox drawer. Absent on the legacy drawer and the dynamic/secondary
  // modal subclasses, which keep their existing behavior.
  declare readonly hasBackdropTarget: boolean;
  declare readonly backdropTarget: HTMLElement;
  declare readonly hasPanelTarget: boolean;
  declare readonly panelTarget: HTMLElement;
  declare readonly drawerPanelTarget: HTMLElement;
  declare readonly frameTargetValue: string;

  private dragging = false;
  private dragStartY = 0;
  private dragDelta = 0;
  private onBeforeFetch!: (event: Event) => void;
  private onFetchError!: (event: Event) => void;

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener("keydown", this.handleKeydown);

    // Sheet only: open immediately with a spinner the moment a request to load
    // our frame starts, so the panel appears instantly instead of after the
    // server round-trip. Cleared on frame-load (see `loaded`) or on error.
    this.onBeforeFetch = (event: Event) => {
      if (!this.isPrefetch(event) && this.isOwnFrame(event)) this.startLoading();
    };
    this.onFetchError = (event: Event) => {
      if (this.isOwnFrame(event)) this.stopLoading();
    };
    document.addEventListener("turbo:before-fetch-request", this.onBeforeFetch);
    document.addEventListener("turbo:fetch-request-error", this.onFetchError);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("turbo:before-fetch-request", this.onBeforeFetch);
    document.removeEventListener("turbo:fetch-request-error", this.onFetchError);
    this.unlockScroll();
  }

  // A late frame response must not reopen a dismissed sheet.
  loaded() {
    const dismissed =
      this.hasPanelTarget && !this.panelTarget.classList.contains("is-open");
    this.stopLoading();
    if (!dismissed) this.open();
  }

  // Turbo 8 prefetches a link's href on hover. That request carries our frame
  // id, but it only warms the cache — nothing renders, so no turbo:frame-load
  // ever follows. Opening on it would leave an empty panel spinning from a mere
  // hover.
  private isPrefetch(event: Event): boolean {
    const headers = (event as CustomEvent).detail?.fetchOptions?.headers;
    return headers?.["X-Sec-Purpose"] === "prefetch";
  }

  private isOwnFrame(event: Event): boolean {
    if (!this.hasPanelTarget || !(event.target instanceof Element)) return false;

    // Turbo dispatches form fetches from the form, including the temporary
    // form it creates for data-turbo-method links outside the destination frame.
    const target = event.target;
    const frameId = target.closest("[data-turbo-frame]")?.getAttribute("data-turbo-frame")
      || target.closest("turbo-frame")?.id;
    return frameId === this.frameTargetValue;
  }

  private startLoading() {
    if (!this.hasPanelTarget) return;
    this.panelTarget.classList.add("is-loading");
    // Open on the initial fetch (panel closed); on a submit it's already open,
    // so don't re-run open() and steal focus / re-lock scroll.
    if (!this.panelTarget.classList.contains("is-open")) this.open();
  }

  private stopLoading() {
    if (this.hasPanelTarget) this.panelTarget.classList.remove("is-loading");
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === "Escape" && this.hasRequiredTargets()) {
      this.close(event);
    }
  }

  private hasRequiredTargets(): boolean {
    // Check if at least one of the required targets exists
    return (
      this.hasInputToggleTarget || this.hasElementTarget || this.hasPanelTarget
    );
  }

  open() {
    if (this.hasPanelTarget) {
      this.openSheet();
    } else if (this.hasInputToggleTarget) {
      this.closeDynamicModal();
      this.inputToggleTarget.checked = true;
    } else {
      this.elementTarget.classList.add("modal-open");
    }
  }

  close(event?: Event, preventDefault: boolean = true) {
    if (preventDefault && event) event.preventDefault();

    if (this.hasPanelTarget) {
      this.closeSheet();
      return;
    }

    if (this.hasInputToggleTarget && this.inputToggleTarget.checked) {
      this.inputToggleTarget.checked = false;
      const frame = document.getElementById(this.frameTargetValue || "drawer_frame");
      if (frame && this.element.contains(frame)) this.closed(frame);
    }

    if (this.hasElementTarget && this.elementTarget.classList.contains("modal-open")) {
      this.elementTarget.classList.remove("modal-open");
      this.closed(this.elementTarget);
    }
  }

  // ---- responsive drawer / bottom sheet (sheet targets present) ----

  private openSheet() {
    this.backdropTarget.classList.add("is-open");
    this.panelTarget.classList.add("is-open");
    this.lockScroll();
    // Move focus into the panel (e.g. the first input). preventScroll keeps the
    // slide-in animation from jumping the off-screen element into view.
    this.panelTarget
      .querySelector<HTMLElement>("[autofocus]")
      ?.focus({ preventScroll: true });
  }

  private closeSheet() {
    if (!this.panelTarget.classList.contains("is-open")) return;
    this.backdropTarget.classList.remove("is-open");
    this.panelTarget.classList.remove("is-open");
    this.panelTarget.classList.remove("is-loading");
    this.unlockScroll();
    this.closed(this.panelTarget);
  }

  private closed(container: HTMLElement) {
    this.dispatch("closed", { prefix: "modal", target: container });
  }

  // The page behind the sheet is pinned by the shared lock (lib/page_lock),
  // which every sheet holds while open.
  private lockScroll() {
    lockPage(this.element);
  }

  private unlockScroll() {
    unlockPage(this.element);
  }

  // ---- swipe-to-dismiss (mobile bottom sheet) ----

  dragStart(event: PointerEvent) {
    if (!this.hasPanelTarget) return;
    this.dragging = true;
    this.dragStartY = event.clientY;
    this.dragDelta = 0;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.panelTarget.style.transition = "none";
  }

  dragMove(event: PointerEvent) {
    if (!this.dragging) return;
    // Only allow dragging downward; ignore upward movement.
    this.dragDelta = Math.max(0, event.clientY - this.dragStartY);
    this.panelTarget.style.transform = `translateY(${this.dragDelta}px)`;
  }

  dragEnd() {
    if (!this.dragging) return;
    this.dragging = false;
    this.panelTarget.style.transition = "";
    const threshold = Math.min(this.panelTarget.offsetHeight * 0.3, 140);
    // Clearing the inline transform lets the CSS class transform take over; if
    // we're closing, removing .is-open animates from the dragged position down.
    this.panelTarget.style.transform = "";
    if (this.dragDelta > threshold) this.close();
  }

  closeWithTurbo(event: Event) {
    this.close(event, false);
  }

  closeBeforeVisit(event: CustomEvent<{ finished?: Promise<unknown> }>) {
    this.close();
    const panel = this.hasPanelTarget ? this.panelTarget : this.drawerPanelTarget;
    // No timer: disabled or cancelled transitions must not hold up navigation.
    event.detail.finished = Promise.allSettled(panel.getAnimations().map((animation) => animation.finished));
  }

  // A submit from inside the sheet started the spinner, but a turbo-stream reply
  // never fires turbo:frame-load, so the submit's end has to clear it.
  async onSubmit(event: TurboStreamEvent) {
    this.stopLoading();
    (await this.allowClose(event)) && this.close(event, false);
  }

  async allowClose(event: TurboStreamEvent) {
    if (!event?.detail?.success) return false;

    const response = await event.detail.fetchResponse.responseHTML;
    const frameTarget = this.frameTargetValue || "drawer_frame";

    return !this.responseTargetsFrame(response, frameTarget);
  }

  private responseTargetsFrame(response: string, frameTarget: string) {
    const escapedFrameTarget = this.escapeRegExp(frameTarget);
    const turboStreamTarget = new RegExp(
      `<turbo-stream\\b[^>]*\\btarget=["']${escapedFrameTarget}["']`,
      "i",
    );
    const turboFrameTarget = new RegExp(
      `<turbo-frame\\b[^>]*\\bid=["']${escapedFrameTarget}["']`,
      "i",
    );

    return turboStreamTarget.test(response) || turboFrameTarget.test(response);
  }

  private escapeRegExp(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  private closeDynamicModal() {
    document.getElementById("dynamic_modal")?.classList.remove("modal-open");
  }
}
